import { useQuery } from '@tanstack/react-query';
import { DetailCard } from '../DetailCard';
import { useTranslate } from '@tolgee/react';
import { History } from 'components/History';
import { queries } from 'services/queries';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useEffect, useMemo } from 'react';
import dayjs from 'lib/dayjs';
import { useToggle } from 'ahooks';
import { Button } from 'antd';
import { MediaItem, MediaViewer } from 'components/MediaViewer';
import { usePlatformResourceURL } from 'hooks/usePlatformResourceURL';

interface OrderReturnHistoryProps {
  returnHistory: Conversation.Trade.ReturnHistory[];
  platform?: General.PlatformUpperCase;
}

interface ReturnHistoryContentProps {
  viewOriginal: boolean;
  updateTime: number;
  reason: string;
  buyerMessage: string;
  buyerImage: string[];
  buyerVideo: { thumbnailUrl: string; videoUrl: string }[];
  platform?: General.PlatformUpperCase;
}

const ReturnHistoryContent = (props: ReturnHistoryContentProps) => {
  const { viewOriginal, updateTime, reason, buyerMessage, buyerImage, buyerVideo, platform } =
    props;
  const timeDisplay = dayjs(Number(updateTime)).format('YYYY-MM-DD HH:mm:ss');
  const { t } = useTranslate();

  const { getImageResource, getVideoResource } = usePlatformResourceURL();

  const systemLanguage = useSystemLanguageValue();

  const waitToTrans = [reason, buyerMessage];

  const { data: translateData } = useQuery({
    ...queries.translates.overseaTranslateBatch(waitToTrans, systemLanguage, 'drawer'),
    enabled: waitToTrans.length > 0,
  });

  const translatedReason = translateData?.[0];
  const translatedBuyerMessage = translateData?.[1];

  const mediaItems = useMemo<MediaItem[]>(() => {
    const images = (buyerImage ?? []).map((src) => {
      const { src: imageSrc, thumb: imageThumb } = getImageResource(
        src,
        platform as General.PlatformUpperCase,
      );
      return {
        type: 'image' as const,
        src: imageSrc,
        thumb: imageThumb,
      };
    });
    const videos = (buyerVideo ?? []).map((item) => {
      if (typeof item === 'string') {
        const { src: videoSrc } = getVideoResource(item, platform as General.PlatformUpperCase);
        return { type: 'video' as const, src: videoSrc };
      }
      const { src: videoSrc, thumb: videoThumb } = getVideoResource(
        item.videoUrl,
        platform as General.PlatformUpperCase,
        item.thumbnailUrl,
      );
      return {
        type: 'video' as const,
        src: videoSrc,
        thumb: videoThumb,
      };
    });
    return [...images, ...videos];
  }, [getImageResource, getVideoResource, platform, buyerImage, buyerVideo]);

  const hasMedia = mediaItems.length > 0;

  return (
    <div className="flex flex-col gap-2 text-[#84868C] text-xs">
      <div>{timeDisplay}</div>
      <div>
        {t('ViewRefund.reasonAfterSales')}: {viewOriginal ? reason : translatedReason}
      </div>
      <div>
        {t('ViewRefund.reasonOthers')}:{viewOriginal ? buyerMessage : translatedBuyerMessage}
      </div>
      <div>
        {hasMedia && (
          <MediaViewer items={mediaItems} thumbSize={48} gap={12} maxThumbs={12} photoClosable />
        )}
      </div>
    </div>
  );
};

export const OrderReturnHistory = (props: OrderReturnHistoryProps) => {
  const { returnHistory, platform } = props;
  const { t } = useTranslate();

  const systemLanguage = useSystemLanguageValue();

  const waitToTrans = returnHistory.map((item) => item.description);

  const [viewOriginal, { toggle: toggleViewOriginal }] = useToggle(true);

  const { isLoading, data: translateData } = useQuery({
    ...queries.translates.overseaTranslateBatch(waitToTrans, systemLanguage, 'drawer'),
    enabled: waitToTrans.length > 0,
  });

  const hasTrans = translateData && translateData.length > 0;

  const items = useMemo(() => {
    if (hasTrans && !viewOriginal) {
      return returnHistory.map((item, index) => {
        const { updateTime, reason, buyerMessage, buyerImage, buyerVideo } = item;
        const translatedDescription = translateData[index];
        return {
          title: translatedDescription,
          description: (
            <ReturnHistoryContent
              viewOriginal={viewOriginal}
              updateTime={updateTime}
              reason={reason}
              buyerMessage={buyerMessage}
              buyerImage={buyerImage}
              buyerVideo={buyerVideo}
              platform={platform}
            />
          ),
        };
      });
    }
    return returnHistory.map((item) => {
      const { description, updateTime, reason, buyerMessage, buyerImage, buyerVideo } = item;
      return {
        title: description,
        description: (
          <ReturnHistoryContent
            viewOriginal={viewOriginal}
            updateTime={updateTime}
            reason={reason}
            buyerMessage={buyerMessage}
            buyerImage={buyerImage}
            buyerVideo={buyerVideo}
            platform={platform}
          />
        ),
      };
    });
  }, [hasTrans, returnHistory, translateData, viewOriginal, platform]);

  useEffect(() => {
    if (translateData?.length) {
      toggleViewOriginal();
    }
  }, [translateData, toggleViewOriginal]);

  return (
    <DetailCard
      title={t('ViewRefund.returnRecord')}
      rightExtra={
        hasTrans && (
          <Button type="link" className="!px-0" onClick={toggleViewOriginal}>
            {viewOriginal ? t('trade.查看翻译') : t('trade.查看原文')}
          </Button>
        )
      }
    >
      <History loading={isLoading} items={items} />
    </DetailCard>
  );
};
